import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Watch, Dollar, Check, Save } from "@mynaui/icons-react";
import { useSnackbar } from "notistack";
import dashboardService from "../../../services/dashboardService";

const GOALS = [
  {
    id: "maximize-uptime",
    title: "Maximize Uptime",
    description: "Prioritize keeping equipment running with minimal downtime",
    icon: <Watch size={32} color="#020618" strokeWidth={1.5} />,
    // API response field name
    apiKey: "maximizeUptime",
  },
  {
    id: "minimize-cost",
    title: "Minimize Cost",
    description: "Prioritize cost-effective maintenance and operations",
    icon: <Dollar size={32} color="#020618" strokeWidth={1.5} />,
    apiKey: "minimizeCost",
  },
];

const INTENSITY_OPTIONS = [
  {
    id: "conservative",
    title: "Conservative",
    description:
      "Cautious approach. Act only when high confidence signals appear.",
    bullets: [
      "Near-reactive maintenance only when urgently needed",
      "Avoid taking preventive actions without strong evidence",
      "Minimize unnecessary work orders",
    ],
  },
  {
    id: "balanced",
    title: "Balanced",
    description: "Smart middle ground with proactive scheduling and timing",
    bullets: [
      "Schedule maintenance with buffer time before failures",
      "Balance cost efficiency with uptime protection",
      "Batch related work to optimize efficiency",
    ],
  },
  {
    id: "aggressive",
    title: "Aggressive",
    description: "Proactive stance. Act early on emerging signals.",
    bullets: [
      "Preemptive actions even with partial confidence signals",
      "Schedule maintenance well ahead of predicted failures",
      "Prioritize uptime over cost considerations",
    ],
  },
];

const GoalCard = ({ goal, isSelected, savedIntensity, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      p: 2,
      border: "1.5px solid",
      borderColor: isSelected ? "#22C55E" : "#E5E7EB",
      borderRadius: "8px",
      cursor: "pointer",
      bgcolor: isSelected ? "#F0FDF4" : "#fff",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: isSelected ? "#22C55E" : "#D1D5DB",
        bgcolor: isSelected ? "#F0FDF4" : "#F9FAFB",
      },
    }}
  >
    {/* Radio indicator */}
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        border: "2px solid",
        borderColor: isSelected ? "#22C55E" : "#D1D5DB",
        bgcolor: isSelected ? "#22C55E" : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        mr: 1.5,
        transition: "all 0.2s ease",
      }}
    >
      {isSelected && (
        <Box
          sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#fff" }}
        />
      )}
    </Box>

    {/* Text */}
    <Box sx={{ flex: 1 }}>
      <Typography
        sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "#1B2A3D" }}
      >
        {goal.title}
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280", mt: 0.25 }}>
        {goal.description}
      </Typography>
      {savedIntensity && (
        <Typography
          sx={{ fontSize: "0.75rem", color: "#22C55E", mt: 0.5, fontWeight: 500 }}
        >
          Current:{" "}
          {savedIntensity.charAt(0).toUpperCase() + savedIntensity.slice(1)}
        </Typography>
      )}
    </Box>

    {/* Icon */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 40,
        height: 40,
        flexShrink: 0,
        ml: 2,
      }}
    >
      {goal.icon}
    </Box>
  </Box>
);

const IntensityCard = ({ option, isSelected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      p: 2.5,
      border: "1px solid",
      borderColor: isSelected ? "#008236" : "#E5E7EB",
      borderRadius: "8px",
      cursor: "pointer",
      bgcolor: isSelected ? "#F0FDF4" : "#fff",
      position: "relative",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: isSelected ? "#008236" : "#D1D5DB",
        bgcolor: isSelected ? "#F0FDF4" : "#F9FAFB",
      },
    }}
  >
    {isSelected && (
      <Check
        size={32}
        color="#008236"
        strokeWidth={1.5}
        style={{ position: "absolute", top: 16, right: 16 }}
      />
    )}
    <Typography
      sx={{ fontWeight: 700, fontSize: "1rem", color: "#1B2A3D", mb: 0.5 }}
    >
      {option.title}
    </Typography>
    <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280", mb: 1.5 }}>
      {option.description}
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {option.bullets.map((bullet, idx) => (
        <Box
          component="li"
          key={idx}
          sx={{
            fontSize: "0.8125rem",
            color: "#4B5563",
            lineHeight: 1.7,
          }}
        >
          {bullet}
        </Box>
      ))}
    </Box>
  </Box>
);

const OptimizationGoalsModal = ({ open, onClose, onSave, plantId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [step, setStep] = useState("goals"); // 'goals' | 'intensity'
  const [selectedGoal, setSelectedGoal] = useState(null);
  // intensities per goal: { 'maximize-uptime': 'balanced', 'minimize-cost': 'conservative' }
  const [intensities, setIntensities] = useState({});
  // original intensities fetched from API (to detect changes)
  const originalIntensities = useRef({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch optimization goals when modal opens
  useEffect(() => {
    if (!open || !plantId) return;

    setLoading(true);
    dashboardService
      .getOptimizationGoals(plantId)
      .then((res) => {
        const data = res.data?.data;
        if (data) {
          const uptime = data.maximizeUptime || null;
          const cost = data.minimizeCost || null;
          // Enforce mutual exclusivity: prefer whichever is set, uptime takes priority if both
          const fetched = uptime
            ? { "maximize-uptime": uptime, "minimize-cost": null }
            : { "maximize-uptime": null, "minimize-cost": cost };
          originalIntensities.current = fetched;
          setIntensities(fetched);
        }
      })
      .catch((err) =>
        console.error("Failed to fetch optimization goals:", err.message),
      )
      .finally(() => setLoading(false));
  }, [open, plantId]);

  // Reset step when modal closes
  useEffect(() => {
    if (!open) {
      setStep("goals");
      setSelectedGoal(null);
    }
  }, [open]);

  const handleGoalSelect = (goalId) => {
    setSelectedGoal(goalId);
    setStep("intensity");
  };

  const handleBack = () => {
    setStep("goals");
    setSelectedGoal(null);
  };

  const handleCancel = () => {
    // Restore original intensities on cancel
    setIntensities(originalIntensities.current);
    setStep("goals");
    setSelectedGoal(null);
    onClose();
  };

  const handleIntensitySelect = (intensityId) => {
    setIntensities((prev) => ({ ...prev, [selectedGoal]: intensityId }));
  };

  const handleSave = () => {
    const otherGoal = GOALS.find((g) => g.id !== selectedGoal);
    const updatedIntensities = {
      ...intensities,
      [otherGoal.id]: null,
    };
    setSaving(true);
    dashboardService
      .updateOptimizationGoals(
        plantId,
        updatedIntensities["maximize-uptime"],
        updatedIntensities["minimize-cost"],
      )
      .then(() => {
        enqueueSnackbar("Optimization goals saved successfully.", {
          variant: "success",
        });
        if (onSave) {
          onSave({
            goal: selectedGoal,
            intensity: updatedIntensities[selectedGoal],
          });
        }
        originalIntensities.current = updatedIntensities;
        setIntensities(updatedIntensities);
        handleBack();
      })
      .catch(() => {
        enqueueSnackbar(
          "Failed to save optimization goals. Please try again.",
          { variant: "error" },
        );
      })
      .finally(() => setSaving(false));
  };

  const selectedGoalData = GOALS.find((g) => g.id === selectedGoal);
  const currentIntensity = selectedGoal ? intensities[selectedGoal] : null;
  const originalIntensity = selectedGoal
    ? originalIntensities.current[selectedGoal]
    : null;
  const hasChanged = currentIntensity !== originalIntensity;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          boxShadow: "0px 20px 60px rgba(0, 0, 0, 0.15)",
          maxWidth: 520,
        },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
            }}
          >
            <CircularProgress size={32} sx={{ color: "#22C55E" }} />
          </Box>
        ) : step === "goals" ? (
          <>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 0.5,
              }}
            >
              <Typography
                sx={{ fontWeight: 700, fontSize: "1.125rem", color: "#1B2A3D" }}
              >
                Optimization Goals
              </Typography>
              <IconButton
                onClick={handleCancel}
                size="small"
                sx={{ color: "#6B7280", mt: -0.5, mr: -0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography sx={{ fontSize: "0.8125rem", color: "#22C55E", mb: 3 }}>
              Choose what to optimize for
            </Typography>

            {/* Goal Options */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {GOALS.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  isSelected={!!intensities[goal.id]}
                  savedIntensity={intensities[goal.id]}
                  onClick={() => handleGoalSelect(goal.id)}
                />
              ))}
            </Box>
          </>
        ) : (
          <>
            {/* Breadcrumb Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 0.5,
              }}
            >
              <Typography sx={{ fontSize: "0.75rem", color: "#9CA3AF" }}>
                Optimization Goals /{" "}
                <Box component="span" sx={{ color: "#6B7280" }}>
                  {selectedGoalData?.title}
                </Box>
              </Typography>
              <IconButton
                onClick={handleCancel}
                size="small"
                sx={{ color: "#6B7280", mt: -0.5, mr: -0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.125rem",
                color: "#1B2A3D",
                mb: 0.5,
              }}
            >
              {selectedGoalData?.title}
            </Typography>
            <Typography sx={{ fontSize: "0.8125rem", color: "#22C55E", mb: 3 }}>
              Choose how strongly to optimize
            </Typography>

            {/* Intensity Options */}
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}
            >
              {INTENSITY_OPTIONS.map((option) => (
                <IntensityCard
                  key={option.id}
                  option={option}
                  isSelected={intensities[selectedGoal] === option.id}
                  onClick={() => handleIntensitySelect(option.id)}
                />
              ))}
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Button
                onClick={handleBack}
                variant="outlined"
                disableElevation
                sx={{
                  color: "#0F172B",
                  borderColor: "#D1D5DB",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  px: 2.5,
                  mr: "auto",
                  boxShadow: "none",
                  "&:hover": {
                    borderColor: "#9CA3AF",
                    bgcolor: "#F9FAFB",
                    boxShadow: "none",
                  },
                }}
              >
                Back
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={!hasChanged || saving}
                startIcon={
                  saving ? (
                    <CircularProgress size={16} sx={{ color: "#9CA3AF" }} />
                  ) : (
                    <Save size={20} color="#F8FAFC" strokeWidth={1.5} />
                  )
                }
                disableElevation
                sx={{
                  bgcolor: "#00A63E",
                  color: "#F8FAFC",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  px: 2.5,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#16A34A", boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: "#E5E7EB", color: "#9CA3AF" },
                }}
              >
                Save Settings
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OptimizationGoalsModal;
